/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React, { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import CountryFlag from '../../../../components/CountryFlag/CountryFlag';
import Cookies from 'js-cookie';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { checkPremiumMember, ensureCurrentUser } from '../../../../util/data';

import {
  Avatar,
  AvatarLarge,
  ExternalLink,
  IconClose,
  IconCollection,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
  OutsideClickHandler,
  PhoneNumber,
} from '../../../../components';

import { setCurrentUserCurrency } from '../../../../ducks/user.duck';
import { currencyData } from '../../../../config/configStripe';
import { useDispatch } from 'react-redux';
import CartIcon from '../../../../components/CartIcon/CartIcon';
import MobileBottomNavbar from '../../../../components/MobileBottomNavbar/MobileBottomNavbar';
import css from './TopbarMobileMenu.module.css';
import { CURRENCY_COOKIE_KEY } from '../../../../util/constants';
import { updateProfile } from '../../../ProfileSettingsPage/ProfileSettingsPage.duck';
import LanguageSelector from '../../../../components/LanguageSelector/LanguageSelector';
import LocationBasedPhoneLink from '../../../../components/LocationBasedPhoneLink/LocationBasedPhoneLink';
import { openStoreCartInURL } from '../../../../util/dateRangeHelpers';

const phoneNumber = process.env.REACT_APP_TEAM_PHONE_NUMBER;

const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route, phoneNumber, className } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId =>
      hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab =>
      hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) ||
      isInboxPage(route?.params?.tab) ||
      isCurrentPage
      ? css.currentPage
      : null;
  };

  if (type === 'phoneNumber') {
    return (
      <PhoneNumber
        phoneNumber={phoneNumber}
        className={classNames(className, css.navigationLink)}
      />
    );
  }

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const CurrencySelector = ({
  currencyOptions,
  onSelect,
  currentUserCurrency,
  isAuthenticated,
}) => {
  const dispatch = useDispatch();
  const [selectedOption, setSelectedOption] = useState({});
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const userInitialCurrencyData = currencyOptions.find(
    o => o.code === currentUserCurrency
  );

  useEffect(() => {
    setSelectedOption(userInitialCurrencyData);
  }, [userInitialCurrencyData]);

  const handleOptionClick = option => {
    setSelectedOption(option);
    setIsDropdownOpen(false);
    onSelect(option.code);

    // Save to cookie
    Cookies.set(CURRENCY_COOKIE_KEY, option.code, {
      expires: 30,
      secure: true,
    });

    // Save to redux
    dispatch(setCurrentUserCurrency(option.code));

    // Save to profile
    if (isAuthenticated) {
      dispatch(updateProfile({ publicData: { currency: option.code } })).catch(
        () => {
          // Error is already handled via Redux state
          // This catch prevents unhandled promise rejection
        }
      );
    }
  };

  const updatedOptions = currencyOptions.reduce((acc, option) => {
    if (option.code === currentUserCurrency) {
      acc.unshift(option);
    } else {
      acc.push(option);
    }
    return acc;
  }, []);

  return (
    <div className={css.dropDownWrapper}>
      <div className={css.dropdown}>
        <OutsideClickHandler onOutsideClick={() => setIsDropdownOpen(false)}>
          <div
            className={classNames(
              css.selectedOption,
              isDropdownOpen ? css.selectedOptionOpen : css.selectedOptionClose
            )}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className={css.userCurrency}>
              <span>
                <CountryFlag
                  code={selectedOption.countryCode}
                  height="20"
                  width="20"
                />
              </span>
              <span>{selectedOption.code}</span>
            </div>
            <span>
              <svg width="12" height="7" viewBox="0 0 12 7" fill="none">
                <path
                  d="M0.51605 0.547944C0.95205 0.101944 1.55905 0.0669441 2.09205 0.547944L6.00005 4.29494L9.90805 0.547944C10.4411 0.0669441 11.0491 0.101944 11.4821 0.547944C11.9181 0.992944 11.8901 1.74494 11.4821 2.16294C11.0761 2.58094 6.78705 6.66494 6.78705 6.66494C6.57005 6.88794 6.28505 6.99994 6.00005 6.99994C5.71505 6.99994 5.43005 6.88794 5.21105 6.66494C5.21105 6.66494 0.92405 2.58094 0.51605 2.16294C0.10805 1.74494 0.0800499 0.992944 0.51605 0.547944Z"
                  fill="#6B7280"
                />
              </svg>
            </span>
          </div>
          {isDropdownOpen && (
            <div className={css.optionsContainer}>
              <div className={css.option}>
                <span>
                  <FormattedMessage id="Topbar.currencyHeading" />
                </span>
              </div>
              {updatedOptions.map(option => (
                <div
                  key={option.code}
                  className={classNames(
                    selectedOption.code == option.code && css.activeOptions,
                    css.option
                  )}
                  onClick={() => {
                    handleOptionClick({
                      code: option.code,
                      countryCode: option.countryCode,
                    });
                  }}
                >
                  <span>
                    <CountryFlag
                      code={option.countryCode}
                      height="20"
                      width="20"
                    />
                  </span>
                  <span>{option.code}</span>
                </div>
              ))}
            </div>
          )}
        </OutsideClickHandler>
      </div>
    </div>
  );
};

const CartLink = ({ itemsCount }) => {
  const history = useHistory();
  const location = useLocation();
  const isStorefrontRoute = /\/rental-owner\/[^/]+/.test(location.pathname);

  if (isStorefrontRoute) {
    return (
      <InlineTextButton
        rootClassName={classNames(css.navigationLink, css.cart)}
        onClick={() => openStoreCartInURL(history, location)}
      >
        <CartIcon className={css.cartIconMobile} itemsCount={itemsCount} />
      </InlineTextButton>
    );
  }

  return (
    <NamedLink name="CartPage" className={classNames(css.navigationLink, css.cart)}>
      <CartIcon className={css.cartIconMobile} itemsCount={itemsCount} />
    </NamedLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    currentUserLocationInfo,
    notificationCount,
    customLinks,
    onLogout,
    onClose,
    currentUserCurrency,
    itemsCount,
    showChangeProfileIcon,
    profilePageLinkProps,
    intl,
    currentListing,
    pageAssetsData,
    pageId,
    onOpenVerificationModal,
  } = props;

  const dispatch = useDispatch();
  const user = ensureCurrentUser(currentUser);
  const isPremiumMember = true; //open for all

  // Copy the original array to avoid direct modification
  const updatedCustomLink = [...customLinks];

  // Map over the updated array to render the components
  const extraLinks = updatedCustomLink.map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text || linkConfig.phoneNumber} // Ensure unique keys
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage
          id="TopbarMobileMenu.signupOrLoginToYourAccount"
          values={{ login, signup }}
        />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.unauthCloseRow}>
          <button className={css.closeMenuXButton} onClick={onClose} aria-label={intl.formatMessage({ id: 'TopbarMobileMenu.closeMenuButton' })}>
            <IconClose size="small" />
          </button>
        </div>
        <div className={css.PrimaryButtonContainer}>
          <NamedLink
            className={classNames(
              css.PrimaryButton,
              location?.pathname.includes('/listing') && css.active
            )}
            name="NewListingPage"
            to={{ search: '?mobilemenu=open' }}
          >
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
        <div className={css.secondaryButtonContainer}>
          <LocationBasedPhoneLink
            className={css.secondaryButton}
            defaultPhoneNumber={phoneNumber}
          >
            {intl.formatMessage({ id: 'Topbar.callUs' })}
          </LocationBasedPhoneLink>
        </div>

        <LanguageSelector
          currentListing={currentListing}
          pageAssetsData={pageAssetsData}
          pageId={pageId}
          currentUserLocationInfo={currentUserLocationInfo}
        />
        <CurrencySelector
          currencyOptions={currencyData}
          onSelect={currency => dispatch(setCurrentUserCurrency(currency))}
          currentUserCurrency={currentUserCurrency}
        />
        <div className={classNames(css.content, css.marginBottom)}>
          <div className={css.customLinksWrapper}>
            <p className={css.accountMenuTitle}>
              <FormattedMessage
                id="TopbarMobileMenu.moreMenuTitle"
                defaultMessage="More Menu"
              />
            </p>
            {extraLinks}
          </div>
        </div>
        <div className={css.authenticationContainer}>
          <div className={css.authenticationLink}>
            <NamedLink name="SignupPage" className={css.signupLink}>
              <FormattedMessage id="TopbarMobileMenu.signupLink" />
            </NamedLink>
          </div>
          {/* <div className={css.divider} /> */}
          <div className={css.authenticationLink}>
            <NamedLink name="LoginPage" className={css.loginLink}>
              <FormattedMessage id="TopbarMobileMenu.loginLink" />
            </NamedLink>
          </div>
        </div>
        <MobileBottomNavbar publishesHeight={false} />
      </div>
    );
  }

  // For authenticated users

  const notificationCountBadge =
    notificationCount > 0 ? (
      <NotificationBadge
        className={css.notificationBadge}
        count={notificationCount}
      />
    ) : null;

  // const displayName = (user && user.id && user.attributes.profile.firstName) || {};
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' &&
      ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage =
      currentPage?.indexOf('InboxPage') === 0 &&
      page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage
      ? css.currentPage
      : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';

  const verificationDone = currentUser?.id
    ? currentUser?.attributes?.emailVerified === true
    : null;

  const { firstName } =
    (currentUser && currentUser.id && currentUser?.attributes.profile) || {};

  return (
    <div className={css.root}>
      <div className={css.profileMenuLinkContent}>
        <div className={css.profileMenuCloseRow}>
          <button className={css.closeMenuXButton} onClick={onClose} aria-label={intl.formatMessage({ id: 'TopbarMobileMenu.closeMenuButton' })}>
            <IconClose size="small" />
          </button>
        </div>
        <div className={css.profileMenuLinkRow}>
          <div className={css.profileMenuLinkContentAvatar}>
            <NamedLink
              className={classNames(css.ProfilemenuLink)}
              name="ProfileSettingsPage"
            >
              <Avatar
                className={css.avatar}
                user={currentUser}
                disableProfileLink
              />
              <IconCollection name="profile_edit" />
            </NamedLink>
          </div>
          <div className={css.profileMenuLinkTextContent}>
            <p className={css.accountName}>
              {currentUser ? firstName : <FormattedMessage id="TopbarDesktop.account" />}
            </p>
            <p className={css.accountEmail}>{currentUser?.attributes?.email}</p>
          </div>
        </div>
      </div>
      {onOpenVerificationModal && (
        <InlineTextButton
          onClick={onOpenVerificationModal}
          rootClassName={css.verificationLink}
        >
          <i className="ri-shield-line"></i>
          <FormattedMessage
            id="TopbarMobileMenu.verifyIdentity"
            defaultMessage="Verify Identity"
          />
        </InlineTextButton>
      )}
      <div className={css.PrimaryButtonContainer}>
        {isPremiumMember ? (
          <NamedLink
            className={classNames(
              css.PrimaryButton,
              location?.pathname.includes('/listing') && css.active
            )}
            name="CreateAIListing"
            to={{ search: '?mobilemenu=open' }}
          >
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        ) : (
          <NamedLink
            className={classNames(
              css.PrimaryButton,
              location?.pathname.includes('/listing') && css.active
            )}
            name="NewListingPage"
            to={{ search: '?mobilemenu=open' }}
          >
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        )}
      </div>
      <div className={css.secondaryButtonContainer}>
        <a href={`tel:${phoneNumber}`} className={css.secondaryButton}>
          {intl.formatMessage({ id: 'Topbar.callUs' })} {phoneNumber}
        </a>
      </div>
      <LanguageSelector
        currentListing={currentListing}
        pageAssetsData={pageAssetsData}
        pageId={pageId}
        currentUserLocationInfo={currentUserLocationInfo}
      />
      <CurrencySelector
        currencyOptions={currencyData}
        onSelect={currency => dispatch(setCurrentUserCurrency(currency))}
        currentUserCurrency={currentUserCurrency}
        currentUserLocationInfo={currentUserLocationInfo}
      />
      <div className={css.content}>
        {/* <span className={css.greeting}>
          <FormattedMessage
            id="TopbarMobileMenu.greeting"
            values={{ displayName }}
          />
        </span> */}

        <div className={css.accountLinksWrapper}>
          <p className={css.accountMenuTitle}>
            <FormattedMessage
              id="TopbarMobileMenu.accountMenuTitle"
              defaultMessage="Account Menu"
            />
          </p>
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('ProgilePage')
            )}
            {...profilePageLinkProps}
          >
            <FormattedMessage id="TopbarMobileMenu.profilePageLink" />
          </NamedLink>

          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('ManageListingsPage')
            )}
            name="ManageListingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('ProfileSettingsPage')
            )}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('AccountSettingsPage')
            )}
            name="AccountSettingsPage"
          >
            <span className={css.tabWithBadge}>
              <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
              {verificationDone === false && (
                <span className={`${css.notificationDotTab} ${css.show}`}>
                  1
                </span>
              )}
            </span>
          </NamedLink>
          <NamedLink
            className={classNames(
              css.navigationLink,
              currentPageClass('StoreSettingsPage')
            )}
            name="StoreSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.storeSettingsLink" />
          </NamedLink>
        </div>

        <div className={css.customLinksWrapper}>
          <p className={css.accountMenuTitle}>
            <FormattedMessage
              id="TopbarMobileMenu.moreMenuTitle"
              defaultMessage="More Menu"
            />
          </p>
          {extraLinks}
        </div>

        <InlineTextButton
          rootClassName={classNames(css.navigationLink, css.logoutButton)}
          onClick={onLogout}
        >
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>
        {/* <div className={css.spacer} /> */}
      </div>
      {/* <div className={css.footer}>
        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div> */}
      <div className={css.closeMenuButtonContainer}>
        <button className={css.closeMenuButton} onClick={onClose}>
          ↑ Close Menu
        </button>
      </div>
      <MobileBottomNavbar publishesHeight={false} />
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  currentUser: null,
  notificationCount: 0,
  currentPage: null,
};

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onClose: func.isRequired,
};

export default TopbarMobileMenu;
